import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, useTk } from '@/lib/i18n';
import { useSelectedCity } from '@/lib/use-city';
import { showInlineAlert } from '@/lib/telegram';
import { getCities, type ConsumerCity } from '@/lib/api';

/**
 * Koom GO 顶部条（Mini App home 页 sticky 头部）。
 *
 * 结构（与 design 草稿一致）：
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  [金边 ⓞ ▾]   团购 二手市场 租车 地产 票务·卡券 消息    [ 🟡🟡 koom go ]
 *   └──────────────────────────────────────────────────────────┘
 *
 * - 背景 `bg-neutral-0`（白色暖底），不是整块黄色。Yellow 是 wayfinding 信号
 *   而非装饰（PRODUCT.md 设计原则 1）。
 * - 城市切换是 Koom Yellow 圆点 + 城市名 + chevron，点击展开列表。
 * - 一级 nav 横向滚动；只有「团购」实际跳转 `/groupons`，其余 5 个
 *   走 `showInlineAlert(tk('common.toast.coming_soon'))`，**不**打任何后端请求
 *   （遵守 plans/koom-go-mini-app-redesign A 口径决议）。
 * - 右上角 logo 是 `koomgo-mark.svg`（双圆眼睛）+ 紧贴的 "koom go" 字标，
 *   颜色用 `text-brand-primary-500`（`currentColor` 注入到 SVG fill）。
 */
export function TopBar(): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-20 flex items-center gap-3 px-4 pt-4 pb-2 bg-neutral-0/95 backdrop-blur-md"
      aria-label="Koom GO top bar"
    >
      <CityPill />
      <TopCategoryStrip />
      <BrandMark />
    </header>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CityPill — 左侧的「金边 ⓞ ▾」。
// 内部维护 cities 列表 + open/close popover；和原 CityPicker 行为一致，
// 只是视觉换成了 design 稿里的「黄色圆点 + 城市名 + chevron」组合。
// ────────────────────────────────────────────────────────────────────────────

function CityPill(): React.ReactElement {
  const locale = useLocale();
  const [selectedCode, setSelectedCode] = useSelectedCity();
  const [cities, setCities] = useState<ConsumerCity[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    getCities(ac.signal)
      .then((res) => setCities(res.items))
      .catch(() => setCities([]));
    return () => ac.abort();
  }, []);

  const current = cities?.find((c) => c.code === selectedCode);
  const label = useMemo(() => {
    if (!current) return selectedCode;
    if (locale === 'km-KH') return current.nameKm;
    if (locale === 'en') return current.nameEn;
    return current.nameZh;
  }, [current, locale, selectedCode]);

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900 transition active:scale-[0.96]"
      >
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 rounded-full bg-brand-primary-500"
        />
        <span>{label}</span>
        <ChevronGlyph open={open} />
      </button>

      {open && cities && cities.length > 0 ? (
        <div
          role="listbox"
          className="absolute top-full left-0 mt-2 z-30 min-w-[160px] rounded-xl border border-neutral-200 bg-neutral-0 shadow-lg overflow-hidden text-sm"
        >
          {cities.map((city) => {
            const isCurrent = city.code === selectedCode;
            return (
              <button
                key={city.code}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => {
                  setSelectedCode(city.code);
                  setOpen(false);
                }}
                className={[
                  'block w-full px-4 py-2.5 text-left transition',
                  isCurrent
                    ? 'bg-brand-primary-50 font-semibold text-brand-primary-700'
                    : 'text-neutral-800 hover:bg-neutral-50',
                ].join(' ')}
              >
                {locale === 'km-KH'
                  ? city.nameKm
                  : locale === 'en'
                    ? city.nameEn
                    : city.nameZh}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TopCategoryStrip — 中部横向 nav 条。
// 6 个一级入口：团购 / 二手市场 / 租车 / 地产 / 票务·卡券 / 消息
// 只有「团购」跳转 `/groupons`，其它 5 个 showInlineAlert(coming_soon)。
// ────────────────────────────────────────────────────────────────────────────

interface TopNavItem {
  id: string;
  labelKey: string;
  /** 如果设置，点击跳转该 route；否则触发 comingSoon toast */
  route?: string;
}

const TOP_NAV_ITEMS: readonly TopNavItem[] = [
  { id: 'groupon',     labelKey: 'nav.top.groupon',     route: '/groupons' },
  { id: 'used_market', labelKey: 'nav.top.used_market' },
  { id: 'car_rental',  labelKey: 'nav.top.car_rental' },
  { id: 'real_estate', labelKey: 'nav.top.real_estate' },
  { id: 'tickets',     labelKey: 'nav.top.tickets' },
  { id: 'messages',    labelKey: 'nav.top.messages' },
];

function TopCategoryStrip(): React.ReactElement {
  const tk = useTk();

  return (
    <nav
      aria-label="primary categories"
      className="flex-1 min-w-0 -mr-1 flex gap-4 overflow-x-auto no-scrollbar"
    >
      {TOP_NAV_ITEMS.map((item) => {
        const label = tk(item.labelKey);
        if (item.route) {
          return (
            <Link
              key={item.id}
              to={item.route}
              className="shrink-0 text-sm font-medium text-neutral-800 hover:text-brand-primary-700 transition-colors"
            >
              {label}
            </Link>
          );
        }
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              showInlineAlert(tk('common.toast.coming_soon'));
            }}
            className="shrink-0 text-sm font-medium text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// BrandMark — 右上角 Koom GO 双圆眼睛 + "koom go" 字标。
// ────────────────────────────────────────────────────────────────────────────

function BrandMark(): React.ReactElement {
  return (
    <div
      className="shrink-0 flex flex-col items-center gap-0.5 text-brand-primary-500"
      role="img"
      aria-label="Koom GO"
    >
      <KoomGoMarkSvg />
      <span className="text-[10px] font-bold tracking-wider text-brand-primary-700 leading-none">
        koom go
      </span>
    </div>
  );
}

/**
 * Inline copy of `apps/telegram-mini-app/src/assets/koomgo-mark.svg`.
 * Inlined so `fill="currentColor"` inherits from parent's `text-brand-primary-500`.
 * Pupils stay locked to `#131009` (neutral.900) — brand identity requirement per DESIGN.md.
 */
function KoomGoMarkSvg(): React.ReactElement {
  return (
    <svg
      width="36"
      height="20"
      viewBox="0 0 56 32"
      fill="none"
      aria-hidden
      className="block"
    >
      <circle cx="18" cy="16" r="14" fill="currentColor" />
      <circle cx="38" cy="16" r="14" fill="currentColor" />
      <circle cx="18" cy="16" r="4.5" fill="#131009" />
      <circle cx="38" cy="16" r="4.5" fill="#131009" />
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Glyphs
// ────────────────────────────────────────────────────────────────────────────

function ChevronGlyph({ open }: { open: boolean }): React.ReactElement {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="currentColor"
      aria-hidden
      className={[
        'text-neutral-500 transition-transform duration-fast',
        open ? 'rotate-180' : '',
      ].join(' ')}
    >
      <path d="M1 3l4 4 4-4z" />
    </svg>
  );
}
